import {
  AlertIcon, CodeIcon, DatabaseIcon, GearIcon, GitCommitIcon, GitPullRequestIcon, HistoryIcon, HomeIcon, KeyIcon, MoonIcon, OrganizationIcon, RepoPushIcon, PlayIcon, PulseIcon,
  RepoIcon, ShieldIcon, SunIcon,
} from "@primer/octicons-react";
import { repoFromPath, rp } from "../api/client";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useChanges, useRepos, useWhoami } from "../api/hooks";
import { StateLabel } from "./StateLabel";
import type { Theme } from "./theme";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Kbd } from "./ui/kbd";

interface Item {
  id: string;
  group: string;
  label: string;
  hint?: ReactNode;
  keywords?: string;
  icon: ReactNode;
  run: () => void;
}

/** The repo the current URL is inside, if any. */
export function useCurrentRepo(): string {
  return repoFromPath(useLocation().pathname);
}

/** Subsequence match; lower is better, -1 is no match. Prefers contiguous runs and early hits. */
function score(query: string, text: string): number {
  if (!query) return 0;
  const t = text.toLowerCase();
  const direct = t.indexOf(query);
  if (direct >= 0) return direct;
  let pos = -1;
  let gaps = 0;
  for (const ch of query) {
    const next = t.indexOf(ch, pos + 1);
    if (next < 0) return -1;
    gaps += next - pos - 1;
    pos = next;
  }
  return 100 + gaps;
}

export function useCommandPaletteHotkey(open: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
}

type Props = { theme: Theme; onToggleTheme: () => void };

export function CommandPalette({ open, onOpenChange, ...props }: Props & { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Palette onClose={() => onOpenChange(false)} {...props} />
      </DialogContent>
    </Dialog>
  );
}

/** Mounted only while the dialog is open, so each opening starts from an empty query. */
function Palette({ onClose, theme, onToggleTheme }: Props & { onClose: () => void }) {
  const nav = useNavigate();
  const repo = useCurrentRepo();
  const repos = useRepos();
  const me = useWhoami();
  const changes = useChanges(repo, "open", !!repo);
  const [query, setQuery] = useState("");

  const items = useMemo(() => {
    const go = (to: string) => () => nav(to);
    const out: Item[] = [];
    if (repo) {
      const tabs: Array<[string, string, ReactNode, string?]> = [
        ["/overview", "Overview", <PulseIcon />, "health main verified agents stats dashboard"],
        ["/ci", "Changes", <GitPullRequestIcon />, "in flight open landed reverted conflict train"],
        ["/ci?view=runs", "CI", <PlayIcon />, "actions checks jobs runs pipelines workflows"],
        ["", "Code", <CodeIcon />],
        ["/commits", "Commits", <GitCommitIcon />, "history log main"],
        ["/activity", "Activity", <HistoryIcon />, "audit log history events"],
        ["/remote", "Remote", <RepoPushIcon />, "github batch pr pull push"],
        ["/settings", "Settings", <GearIcon />, "general description origin import"],
        ["/settings/policy", "Settings: Policy", <ShieldIcon />, "risk tiers protected paths limits budget"],
        ["/settings/remote", "Settings: Remote", <RepoPushIcon />, "github pr add remove batch integration"],
        ["/settings/storage", "Settings: Storage", <DatabaseIcon />, "compact gc rebuild flush maintenance"],
        ["/settings/danger", "Settings: Danger zone", <AlertIcon />, "freeze unfreeze frozen paths"],
      ];
      for (const [to, label, icon, keywords] of tabs) out.push({ id: `tab${to}`, group: repo, label, keywords, hint: `${repo}${to || "/"}`, icon, run: go(`${rp(repo)}${to}`) });
      for (const c of changes.data?.changes ?? []) {
        const subject = c.subject ?? c.message.split("\n", 1)[0];
        out.push({
          id: `change-${c.id}`,
          group: "Open changes",
          label: subject,
          keywords: `${c.id} ${c.agent_id} ${c.task_id ?? ""}`,
          hint: <StateLabel state={c.state} />,
          icon: <GitPullRequestIcon />,
          run: go(`${rp(repo)}/changes/${c.id}`),
        });
      }
    }
    for (const r of repos.data ?? []) {
      out.push({ id: `repo-${r.name}`, group: "Repositories", label: r.slug, keywords: r.description, hint: r.description, icon: <RepoIcon />, run: go(rp(r.slug, r.owner)) });
    }
    out.push(
      { id: "home", group: "Navigate", label: "Your profile", keywords: "home dashboard repositories", icon: <HomeIcon />, run: go("/profile") },
      { id: "new-repo", group: "Navigate", label: "New repository", keywords: "create repo", icon: <RepoIcon />, run: go("/new") },
      { id: "new-org", group: "Navigate", label: "New organization", keywords: "create org team", icon: <OrganizationIcon />, run: go("/orgs/new") },
    );
    if (!me.data || me.data.sub === "anonymous") out.push({ id: "login", group: "Navigate", label: "Sign in with a token", icon: <KeyIcon />, run: go("/login") });
    out.push({
      id: "theme",
      group: "Preferences",
      label: theme === "dark" ? "Switch to light theme" : "Switch to dark theme",
      keywords: "theme dark light appearance",
      icon: theme === "dark" ? <SunIcon /> : <MoonIcon />,
      run: onToggleTheme,
    });
    return out;
  }, [repo, repos.data, changes.data, me.data, theme, nav, onToggleTheme]);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return items;
    return items
      .map((it) => ({ it, s: score(q, `${it.label} ${it.keywords ?? ""}`) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => a.s - b.s)
      .map((x) => x.it);
  }, [items, q]);

  /** Without a query, consecutive items of one group share a heading; with one, results are ranked flat. */
  const sections = useMemo(() => {
    if (q) return [{ group: "", items: results }];
    const out: Array<{ group: string; items: Item[] }> = [];
    for (const it of results) {
      if (out.at(-1)?.group === it.group) out.at(-1)!.items.push(it);
      else out.push({ group: it.group, items: [it] });
    }
    return out;
  }, [q, results]);

  const first = results[0]?.id ?? "";
  const [selected, setSelected] = useState(first);
  useEffect(() => setSelected(first), [q, first]);

  const pick = (it: Item) => {
    onClose();
    it.run();
  };

  return (
    <Command shouldFilter={false} value={selected} onValueChange={setSelected} loop>
      <CommandInput autoFocus value={query} onValueChange={setQuery} placeholder="Jump to a repo, change, page, or action…" aria-label="Search">
        <Kbd>esc</Kbd>
      </CommandInput>
      <CommandList>
        <CommandEmpty>No matches for “{query}”.</CommandEmpty>
        {sections.map((s, i) => (
          <CommandGroup key={`${s.group}-${i}`} heading={s.group || undefined}>
            {s.items.map((it) => (
              <CommandItem key={it.id} value={it.id} onSelect={() => pick(it)}>
                <span className="inline-flex shrink-0 text-muted-foreground">{it.icon}</span>
                <span className="truncate text-foreground">{it.label}</span>
                <span className="ml-auto max-w-[45%] truncate text-xs text-faint">{it.hint ?? (q ? it.group : null)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
      <div className="flex gap-4 border-t bg-muted px-4 py-2 text-xs text-muted-foreground [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1">
        <span>
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> navigate
        </span>
        <span>
          <Kbd>↵</Kbd> open
        </span>
        <span className="ml-auto">
          <Kbd>⌘</Kbd>
          <Kbd>K</Kbd> anywhere
        </span>
      </div>
    </Command>
  );
}
